import { Button, Card, Flex, Modal, Tag, Tooltip, Typography } from 'antd';
import { FiAlertCircle, FiCheckCircle } from 'react-icons/fi';

import BaseText from '@components/typography/BaseText';

import { TRAIN_MODAL } from './trainModalContent';

const StatusIcon = ({ ok }) => (
	<Typography.Text type={ok ? 'success' : 'warning'}>
		{ok ? <FiCheckCircle aria-hidden="true" /> : <FiAlertCircle aria-hidden="true" />}
	</Typography.Text>
);

// A check's state: the icon and the figure share the colour, the label stays plain.
const CheckRow = ({ label, value, met }) => (
	<Flex align="center" gap="small">
		<StatusIcon ok={met} />
		<BaseText style={{ flex: 1, minWidth: 0 }}>{label}</BaseText>
		<Typography.Text strong type={met ? 'success' : 'warning'}>
			{value}
		</Typography.Text>
	</Flex>
);

/**
 * The modal behind the annotation toolbar's Train New Object button, for the active
 * object. Blocked ("Not enough training data yet") lists what the summary still
 * wants; ready ("Create a new object from this work?") offers the create form. Both
 * can save the open tile instead, which keeps annotating for later.
 *
 * Retraining the active object itself waits for the node train route, so a ready
 * object can only seed a new one for now.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {ReturnType<import('./trainModalContent').trainModalContent>} props.content
 * @param {() => void} props.onClose
 * @param {() => void} props.onSave - saves the open tile (or image)
 * @param {boolean} [props.canSave] - the open tile has unsaved boxes (an untouched tile is
 *   marked empty from the toolbar, not from here)
 * @param {() => void} props.onCreate - opens the create form
 * @param {string|null} [props.createDisabledReason] - why Create is off (unsaved boxes, or the counts
 *   refetching)
 */
const TrainObjectModal = ({
	open,
	content,
	onClose,
	onSave,
	canSave = false,
	onCreate,
	createDisabledReason = null,
}) => {
	if (!content) return null;
	const { kind, title, body, checks, labelRows } = content;
	const blocked = kind === TRAIN_MODAL.BLOCKED;

	// A Flex, not antd's array: the tooltip's span would break the footer's button spacing.
	const footer = (
		<Flex justify="end" gap="small">
			<Button onClick={onSave} disabled={!canSave}>
				{blocked ? 'Save and come back later' : 'Save'}
			</Button>
			{blocked ? (
				<Button type="primary" onClick={onClose}>
					Keep annotating
				</Button>
			) : (
				<Tooltip title={createDisabledReason}>
					<span>
						<Button
							type="primary"
							onClick={onCreate}
							disabled={Boolean(createDisabledReason)}>
							Create a new object
						</Button>
					</span>
				</Tooltip>
			)}
		</Flex>
	);

	return (
		<Modal
			open={open}
			title={
				<Flex align="center" gap="small">
					<StatusIcon ok={!blocked} />
					{title}
				</Flex>
			}
			onCancel={onClose}
			footer={footer}
			destroyOnHidden>
			<Flex vertical gap="middle">
				<BaseText type="secondary">{body}</BaseText>

				{checks.length > 0 && (
					<Card size="small">
						<Flex vertical gap="small">
							{checks.map(({ key, ...row }) => (
								<CheckRow key={key} {...row} />
							))}
						</Flex>
					</Card>
				)}

				{labelRows.length > 0 && (
					<Card size="small" title="Per label">
						<Flex vertical gap="small">
							{labelRows.map(row => (
								<Flex key={row.key} align="center" gap="small">
									<BaseText ellipsis style={{ flex: 1, minWidth: 0 }}>
										{row.label}
									</BaseText>
									<BaseText size="sm" type="secondary">
										{row.counts}
									</BaseText>
									{row.canTrain ? (
										<Tag color="success">can train</Tag>
									) : (
										// Focusable, so the shortfall reaches the keyboard too.
										<Tooltip title={row.missing} trigger={['hover', 'focus']}>
											<Tag color="warning" tabIndex={0}>
												not yet
											</Tag>
										</Tooltip>
									)}
								</Flex>
							))}
						</Flex>
					</Card>
				)}
			</Flex>
		</Modal>
	);
};

export default TrainObjectModal;
